<div class="header-root" (mouseleave)="startCloseTimer()">

  <!-- ── Announcement Bar ─────────────────────────────────────────────────── -->
  <!-- Renders ONLY when a real announcement is supplied — see the
  `announcement` @Input. No announcement, no wasted header height. -->
  @if (showAnnouncement) {
    <div class="ann-bar" role="status">
      @if (announcementBadge) {
        <span class="ann-badge">{{ announcementBadge }}</span>
      }
      {{ announcement }}
      <button class="ann-close" (click)="dismissAnnouncement()" aria-label="סגור הודעה">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18"/>
        </svg>
      </button>
    </div>
  }

  <!-- ── Main Bar ───────────────────────────────────────────────────────────── -->
  <!-- Single row: brand · search · grouped account actions · one CTA.
  The old separate "top strip" row (privacy/terms/about/contact +
  a second, competing login control) is gone — those secondary links
  now live in the footer and the mobile drawer, and login is merged
  into the account cluster below so account actions sit together. -->
  <header class="main-bar">

    <a class="logo-wrap" [routerLink]="['']" aria-label="Sale4ALL — לדף הבית">
      <div class="logo-box" aria-hidden="true"><img src="assets/img/sale4all-icon.png" alt="" width="38" height="38"></div>
      <div class="logo-name">
        <strong>Sale4ALL</strong>
      </div>
    </a>

    <!-- Primary task on the page: search. Real input, submits to /search. -->
    <form class="search-wrap" role="search" (ngSubmit)="submitSearch()">
      <label class="sr-only" for="header-search">חיפוש מודעות</label>
      <div class="search-inner">
        <svg class="search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>
        </svg>
        <input id="header-search"
          type="search"
          name="q"
          autocomplete="off"
          placeholder="מה תרצו לחפש? רכב, דירה, מוצר יד שנייה…"
          (focus)="openSearchPage()"
          [(ngModel)]="searchQuery"
          [ngModelOptions]="{ standalone: true }" />
          <!-- No aria-label: the visible text IS the accessible name.
          An aria-label that differs from visible text breaks
          voice control (WCAG 2.5.3 "Label in Name"). -->
          <button class="search-btn" type="submit">חיפוש</button>
        </div>
      </form>

      <div class="header-actions">
        <!-- Account cluster — notifications, cart, and account together -->
        <nav class="account-cluster" aria-label="פעולות חשבון">
          <!-- Plain wrapper, NOT an <a>: app-notification-bell renders its
          own <button>, so an anchor here would nest one interactive
          element inside another and give it a competing label. -->
          <div class="icon-btn icon-btn--host">
            <app-notification-bell></app-notification-bell>
          </div>

          <!-- routerLink, not (click): an <a> with no href is not
          focusable and cannot be activated by keyboard, so the
          focus styles below would never have applied. -->
          @if (!AppService.isMobile()) {
            <a class="icon-btn" title="מועדפים" aria-label="מועדפים"
              [routerLink]="['/personal/wishlist']">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M20.8 5.6a5 5 0 00-7.1 0L12 7.3l-1.7-1.7a5 5 0 10-7.1 7.1L12 21l8.8-8.3a5 5 0 000-7.1z"/>
              </svg>
            </a>
          }
          @if (!AppService.isMobile()) {
            <a class="icon-btn cart-icon-btn" title="עגלת קניות"
                   [attr.aria-label]="cartService.count > 0
                        ? 'עגלת קניות, ' + cartService.count + ' פריטים'
                        : 'עגלת קניות'"
              [routerLink]="['/cart']">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              @if (cartService.count > 0) {
                <span class="cart-badge" aria-hidden="true">{{ cartService.count }}</span>
              }
            </a>
          }
          @if (!AppService.isMobile()) {
            @if (isLogin()) {
              <!-- Avatar · greeting · VIP/regular badge + account dropdown -->
              <app-account-menu (logout)="logout()"></app-account-menu>
            } @else {
              <button class="user-btn" aria-label="התחברות או הרשמה" (click)="openLogin()">
                <span class="avatar" aria-hidden="true">
                  <i class="fa fa-user" aria-hidden="true"></i>
                </span>
                <span class="user-btn__label">התחברות</span>
              </button>
            }
          }
        </nav>

        <!-- The single primary action on the whole header -->
        <button class="cta-btn" (click)="navigateTo('/publish')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
            stroke-linecap="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          <span>פרסם מודעה</span>
        </button>

        <button class="mobile-menu-btn" (click)="toggleMobileMenu()"
          aria-label="פתח תפריט" [attr.aria-expanded]="mobileMenuOpen">
          <i class="fa fa-bars" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <!-- ── Nav Bar ────────────────────────────────────────────────────────────── -->
    <div class="nav-wrap">
      <div class="cat-strip">

        <!-- All Categories -->
        <button class="all-cats-btn" type="button"
          [class.active]="allCatsOpen"
          [attr.aria-expanded]="allCatsOpen"
          (click)="toggleAllCats()">
          כל הקטגוריות
        </button>
        <div class="strip-divider"></div>

        <!-- Category Links -->
        <nav class="cat-nav" aria-label="קטגוריות ראשיות">
          @for (cate of mainCategories; track cate) {
            @if (cate.important) {
              <a class="cat-link"
                [class.on]="hoveredCategory === cate"
                href="{{ '/shop/' + cate.nameEng }}"
                (mouseenter)="onCatHover(cate)"
                (mouseleave)="startCloseTimer()">
                <span class="cat-ico" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                    <path [attr.d]="getCategoryIcon(cate.category)"/>
                  </svg>
                </span>
                <span class="cat-lbl">{{ cate.name }}</span>
                @if (cate.subcategories?.length) {
                  <i class="arr fa fa-angle-down" aria-hidden="true"></i>
                }
              </a>
            }
          }
        </nav>
      </div>

      <!-- All Cats Dropdown -->
      <div class="all-drop" [class.show]="allCatsOpen">
        @for (cate of mainCategories; track cate) {
          <a class="all-item" href="{{ '/shop/' + cate.nameEng }}">
            <div class="all-ico" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                <path [attr.d]="getCategoryIcon(cate.category)"/>
              </svg>
            </div>
            <span>{{ cate.name }}</span>
            @if (cate.subcategories?.length) {
              <small>{{ cate.subcategories.length }} קטגוריות</small>
            }
          </a>
        }
      </div>

      <!-- Mega Menu -->
      <div class="mega-menu"
        [class.show]="hoveredCategory && hoveredCategory.subcategories?.length"
        (mouseenter)="cancelCloseTimer()"
        (mouseleave)="startCloseTimer()">

        @if (hoveredCategory?.subcategories?.length) {
          <!-- Left Sidebar -->
          <div class="mega-side">
            @for (sub of hoveredCategory.subcategories; track sub) {
              <a class="msi"
                [class.active]="hoveredSub === sub"
                href="{{ '/shop/' + hoveredCategory.nameEng + '/category/' + sub.id }}"
                (mouseenter)="hoveredSub = sub">
                <span class="msi-ico">{{ getSubCategoryIcon(sub.subCategory) }}</span>
                <span class="msi-name">{{ sub.name }}</span>
                <i class="fa fa-angle-left msi-arr" aria-hidden="true"></i>
              </a>
            }
          </div>
          <!-- Right Body -->
          <div class="mega-body">
            <div class="mega-body-head">
              <span class="mbh-parent">{{ hoveredCategory.name }}</span>
              <i class="fa fa-angle-left mbh-sep" aria-hidden="true"></i>
              <span class="mbh-current">{{ (hoveredSub || hoveredCategory.subcategories[0])?.name }}</span>
            </div>
            <!-- Grid of all subcategories -->
            <div class="mega-grid" [class.wide]="hoveredCategory.subcategories.length > 6">
              @for (sub of hoveredCategory.subcategories; track sub) {
                <a class="mgi"
                  [class.highlight]="hoveredSub === sub"
                  href="{{ '/shop/' + hoveredCategory.nameEng + '/category/' + sub.id }}"
                  (mouseenter)="hoveredSub = sub">
                  <div class="mgi-ico">{{ getSubCategoryIcon(sub.subCategory) }}</div>
                  <span>{{ sub.name }}</span>
                </a>
              }
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Placeholder prevents content jump when nav becomes fixed -->
    <div class="nav-placeholder"></div>

    <!-- ── Mobile Overlay + Drawer ───────────────────────────────────────────── -->
    <div class="mobile-overlay" [class.open]="mobileMenuOpen" (click)="toggleMobileMenu()"></div>

    <div class="mobile-drawer" [class.open]="mobileMenuOpen">
      <div class="drawer-head">
        <div class="drawer-logo">
          <div class="dl-icon" aria-hidden="true"><i class="fa fa-tag"></i></div>
          <strong>Sale4ALL</strong>
        </div>
        <button class="drawer-close" (click)="toggleMobileMenu()" aria-label="סגור תפריט"><i class="fa fa-times" aria-hidden="true"></i></button>
      </div>

      <form class="drawer-search" role="search" (ngSubmit)="submitSearch()">
        <label class="sr-only" for="drawer-search">חיפוש מודעות</label>
        <div class="ds-inner">
          <i class="fa fa-search" aria-hidden="true"></i>
          <input id="drawer-search" type="search" name="q" autocomplete="off"
            placeholder="מה תרצו לחפש?"
            [(ngModel)]="searchQuery"
            [ngModelOptions]="{ standalone: true }" />
          </div>
        </form>

        <nav class="drawer-nav" aria-label="קטגוריות">
          @for (cate of mainCategories; track cate) {
            @if (cate.important) {
              <div class="drawer-item">
                <!-- The row itself is no longer clickable: it used to be a
                <div> with (click), which keyboard users could never
                  reach. The link and the expand toggle are now two
                  separate, properly focusable controls. -->
                  <div class="di-head">
                    <div class="di-title">
                      <span class="di-ico" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                          <path [attr.d]="getCategoryIcon(cate.category)"/>
                        </svg>
                      </span>
                      <a href="{{ '/shop/' + cate.nameEng }}">{{ cate.name }}</a>
                    </div>
                    @if (cate.subcategories?.length) {
                      <button type="button"
                        class="di-toggle"
                        [class.open]="cate.isOpen"
                        [attr.aria-expanded]="!!cate.isOpen"
                        [attr.aria-label]="(cate.isOpen ? 'סגור' : 'פתח') + ' תת־קטגוריות של ' + cate.name"
                        (click)="cate.isOpen = !cate.isOpen">
                        <i class="fa fa-chevron-down" aria-hidden="true"></i>
                      </button>
                    }
                  </div>
                  @if (cate.subcategories?.length && cate.isOpen) {
                    <div class="drawer-subs">
                      @for (sub of cate.subcategories; track sub) {
                        <a
                          href="{{ '/shop/' + cate.nameEng + '/category/' + sub.id }}"
                          class="drawer-sub">
                          <span>{{ getSubCategoryIcon(sub.subCategory) }}</span>{{ sub.name }}
                        </a>
                      }
                    </div>
                  }
                </div>
              }
            }
          </nav>

          <div class="drawer-foot">
            <!-- Account actions grouped together, mirroring the desktop cluster -->
            @if (isLogin()) {
              <!-- Greeting + membership live here on mobile (no room in the bar);
              the card itself links to the account page. -->
              <app-account-menu variant="summary" (navigated)="toggleMobileMenu()"></app-account-menu>
              <button class="df-outline" (click)="navigateTo('/personal/wishlist'); toggleMobileMenu()">
                <i class="fa fa-heart-o" aria-hidden="true"></i> מועדפים
              </button>
              <button class="df-outline" (click)="navigateTo('/cart'); toggleMobileMenu()">
                <i class="fa fa-shopping-bag" aria-hidden="true"></i> עגלת קניות
              </button>
            }
            @if (!isLogin()) {
              <button class="df-outline" (click)="openLogin()">
                <i class="fa fa-user" aria-hidden="true"></i> התחברות / הרשמה
              </button>
            }

            <button class="df-solid" (click)="navigateTo('/publish'); toggleMobileMenu()">
              <i class="fa fa-plus" aria-hidden="true"></i> פרסם מודעה
            </button>

            @if (isLogin()) {
              <button class="df-outline df-quiet" (click)="logout()">
                <i class="fa fa-sign-out" aria-hidden="true"></i> התנתקות
              </button>
            }
            <div class="df-legal-links">
              <a class="df-about" [routerLink]="['/about']" (click)="toggleMobileMenu()">
                <i class="fa fa-info-circle" aria-hidden="true"></i> {{ 'sale4you.about.title' | translate }}
              </a>
              <a class="df-about" [routerLink]="['/contact']" (click)="toggleMobileMenu()">
                <i class="fa fa-envelope-o" aria-hidden="true"></i> {{ 'sale4you.contact.title' | translate }}
              </a>
              <a class="df-about" [routerLink]="['/privacy']" (click)="toggleMobileMenu()">
                <i class="fa fa-shield" aria-hidden="true"></i> {{ 'sale4you.privacy.title' | translate }}
              </a>
              <a class="df-about" [routerLink]="['/terms']" (click)="toggleMobileMenu()">
                <i class="fa fa-file-text-o" aria-hidden="true"></i> {{ 'sale4you.terms.title' | translate }}
              </a>
              <a class="df-about" href="/assets/readabler/accessibility-statement.html" target="_blank" rel="noopener" (click)="toggleMobileMenu()">
                <i class="fa fa-universal-access" aria-hidden="true"></i> הצהרת נגישות
              </a>
            </div>
            <!-- Icon-only links need an accessible name — the <i> carries no
            text, so without these a screen reader announced only "link". -->
            <div class="drawer-socials">
              <a href="#" aria-label="פייסבוק"><i class="fa fa-facebook" aria-hidden="true"></i></a>
              <a href="#" aria-label="אינסטגרם"><i class="fa fa-instagram" aria-hidden="true"></i></a>
              <a href="#" aria-label="טוויטר"><i class="fa fa-twitter" aria-hidden="true"></i></a>
            </div>
          </div>
        </div>

      </div>
